import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Text,
  Spinner,
  SpinnerSize,
  MessageBar,
  MessageBarType,
  DetailsList,
  IColumn,
  DetailsListLayoutMode,
  SelectionMode,
  TextField,
  Dropdown,
  IDropdownOption,
  PrimaryButton,
  DefaultButton,
  Callout,
  DirectionalHint,
  mergeStyleSets,
} from "@fluentui/react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Eye, UsersThree, Plugs, Star, ChartBar, TrendUp } from '@phosphor-icons/react';
import { boroondaraPalette } from "../theme/boroondaraTheme";
import { accessService } from "../services/accessService";

/* ── Types ─────────────────────────────────────────── */
interface ActivityRow {
  id: number;
  user_upn: string;
  user_name: string | null;
  method: string;
  path: string;
  page: string | null;
  status_code: number;
  duration_ms: number;
  ip_address: string | null;
  created_at: string;
}

interface DailyView {
  day: string;
  views: number;
}

interface AnalyticsKpis {
  totalPageViews: number;
  uniqueUsers: number;
  uniqueSessions: number;
  avgResponseMs: number;
  mostActivePage: string;
}

interface AnalyticsData {
  kpis: AnalyticsKpis;
  dailyViews: DailyView[];
  activity: ActivityRow[];
  totalCount: number;
  pages: string[];
  users: { user_upn: string; user_name: string | null }[];
}

interface PortalUser {
  upn: string;
  displayName?: string;
  role?: string;
  isActive?: boolean;
}

/* ── Styles ────────────────────────────────────────── */
const styles = mergeStyleSets({
  page: {
    padding: 32,
    minHeight: "calc(100vh - 52px)",
    background: "#F5F5F5",
    "@media (max-width: 768px)": { padding: 16 },
  } as any,
  container: { maxWidth: 1240, margin: "0 auto" },
  headerRow: { marginBottom: 24 },
  title: {
    color: boroondaraPalette.text,
    fontWeight: 800 as any,
    letterSpacing: "-0.02em",
  },
  subtitle: {
    color: boroondaraPalette.text2,
    marginTop: 6,
    maxWidth: 720,
    lineHeight: "1.6",
  },
  // Filters
  filterBar: {
    display: "flex",
    flexWrap: "wrap" as any,
    gap: 12,
    alignItems: "flex-end",
    marginBottom: 24,
    padding: 16,
    background: "#FFFFFF",
    borderRadius: 4,
    border: "1px solid #E1E1E1",
  },
  filterField: { minWidth: 140, maxWidth: 180 },
  // KPIs
  kpiRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 16,
    marginBottom: 24,
    "@media (max-width: 768px)": { gridTemplateColumns: "repeat(2, 1fr)", gap: 10 },
  } as any,
  kpiCard: {
    padding: 20,
    borderRadius: 4,
    background: "#FFFFFF",
    border: "1px solid #E1E1E1",
    boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
    "@media (max-width: 768px)": { padding: 14 },
  } as any,
  kpiIconWrap: {
    width: 36,
    height: 36,
    borderRadius: 6,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 10,
  },
  kpiLabel: {
    color: boroondaraPalette.text3,
    fontSize: 11,
    letterSpacing: "0.06em",
    textTransform: "uppercase" as any,
    fontWeight: 600 as any,
    marginBottom: 6,
  },
  kpiValue: {
    color: boroondaraPalette.text,
    fontWeight: 700 as any,
    fontSize: 26,
    lineHeight: "32px",
    letterSpacing: "-0.02em",
    "@media (max-width: 768px)": { fontSize: 20, lineHeight: "26px" },
  } as any,
  // Chart
  chartCard: {
    padding: 24,
    borderRadius: 4,
    background: "#FFFFFF",
    border: "1px solid #EDEDED",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    marginBottom: 24,
    "@media (max-width: 768px)": { padding: 14 },
  } as any,
  chartTitle: {
    color: boroondaraPalette.text,
    fontWeight: 600 as any,
    fontSize: 15,
    marginBottom: 16,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  // Table
  tableCard: {
    padding: 24,
    borderRadius: 4,
    background: "#FFFFFF",
    border: "1px solid #EDEDED",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    marginBottom: 24,
    "@media (max-width: 768px)": { padding: 14 },
  } as any,
  pill: {
    display: "inline-block",
    padding: "2px 10px",
    borderRadius: 12,
    fontSize: 11,
    fontWeight: 600 as any,
    background: "#d9f4d7",
    color: "#2e873d",
  },
  statusOk: { color: "#2e873d", fontWeight: 600 as any },
  statusErr: { color: "#d32f2f", fontWeight: 600 as any },
  loadMore: { display: "flex", justifyContent: "center", marginTop: 16 },
  centerWrap: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 400,
  },
  noData: {
    color: boroondaraPalette.text3,
    fontSize: 13,
    textAlign: "center" as any,
    padding: 40,
  },
  // User search suggestions
  userSearchWrap: { position: "relative" as any, minWidth: 160, maxWidth: 220 },
  suggestionList: {
    maxHeight: 220,
    overflowY: "auto" as any,
    padding: 4,
  },
  suggestionItem: {
    display: "flex",
    flexDirection: "column" as any,
    padding: "8px 12px",
    cursor: "pointer",
    borderRadius: 4,
    transition: "background 0.1s",
    selectors: {
      ":hover": { background: "#F0F6F0" },
    },
  } as any,
  suggestionName: {
    fontSize: 13,
    fontWeight: 600 as any,
    color: boroondaraPalette.text,
  },
  suggestionUpn: {
    fontSize: 11,
    color: boroondaraPalette.text3,
  },
  suggestionRole: {
    display: "inline-block",
    fontSize: 10,
    fontWeight: 600 as any,
    padding: "1px 6px",
    borderRadius: 8,
    marginLeft: 6,
  },
  suggestionEmpty: {
    padding: "12px 16px",
    fontSize: 12,
    color: boroondaraPalette.text3,
    textAlign: "center" as any,
  },
});

const tooltipStyle = {
  backgroundColor: "#FFFFFF",
  border: "1px solid #E1E1E1",
  borderRadius: 4,
  color: "#232323",
  fontSize: 12,
};

/* ── Component ─────────────────────────────────────── */
export default function Analytics() {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [accessChecked, setAccessChecked] = useState(false);

  // Filters
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [userFilter, setUserFilter] = useState("");
  const [pageFilter, setPageFilter] = useState("");

  // User search
  const [userSearch, setUserSearch] = useState("");
  const [showUserSuggestions, setShowUserSuggestions] = useState(false);
  const [allPortalUsers, setAllPortalUsers] = useState<PortalUser[]>([]);
  const userSearchRef = useRef<HTMLDivElement>(null);

  // Pagination
  const [offset, setOffset] = useState(0);
  const PAGE_SIZE = 50;

  // Check access (admins always have access; non-admins need canAccessAnalytics toggle)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await accessService.get<{ role?: string; canAccessAnalytics?: boolean }>("/me");
        if (!alive) return;
        setHasAccess(res.data.role === "admin" || res.data.canAccessAnalytics === true);
      } catch {
        if (alive) setHasAccess(false);
      } finally {
        if (alive) setAccessChecked(true);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Load all portal users for search suggestions
  useEffect(() => {
    if (!hasAccess) return;
    let alive = true;
    (async () => {
      try {
        const res = await accessService.get<PortalUser[]>("/admin/users");
        if (alive) setAllPortalUsers(res.data);
      } catch {
        // Non-critical — analytics activity users will still work as fallback
      }
    })();
    return () => { alive = false; };
  }, [hasAccess]);

  // Merge admin users + activity users for suggestions
  const mergedUsers = useMemo(() => {
    const map = new Map<string, { upn: string; name: string; role?: string }>();
    // Portal users from admin API
    for (const u of allPortalUsers) {
      map.set(u.upn, { upn: u.upn, name: u.displayName || u.upn.split("@")[0], role: u.role });
    }
    // Activity users (may include users not in admin list)
    for (const u of (data?.users || [])) {
      if (!map.has(u.user_upn)) {
        map.set(u.user_upn, { upn: u.user_upn, name: u.user_name || u.user_upn.split("@")[0] });
      }
    }
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [allPortalUsers, data?.users]);

  // Filter suggestions by search text
  const filteredSuggestions = useMemo(() => {
    const q = userSearch.toLowerCase().trim();
    if (!q) return mergedUsers;
    return mergedUsers.filter(
      (u) => u.upn.toLowerCase().includes(q) || u.name.toLowerCase().includes(q)
    );
  }, [userSearch, mergedUsers]);

  const loadData = useCallback(
    async (newOffset = 0) => {
      try {
        setLoading(true);
        setError(null);
        const params = new URLSearchParams();
        if (dateFrom) params.set("dateFrom", dateFrom);
        if (dateTo) params.set("dateTo", dateTo);
        if (userFilter) params.set("userUpn", userFilter);
        if (pageFilter) params.set("page", pageFilter);
        params.set("limit", String(PAGE_SIZE));
        params.set("offset", String(newOffset));

        const res = await accessService.get<AnalyticsData>(
          `/analytics/activity?${params.toString()}`
        );
        if (newOffset > 0 && data) {
          setData({
            ...res.data,
            activity: [...data.activity, ...res.data.activity],
          });
        } else {
          setData(res.data);
        }
        setOffset(newOffset);
      } catch (err: any) {
        setError(err?.response?.data?.error || err?.message || "Failed to load analytics");
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dateFrom, dateTo, userFilter, pageFilter]
  );

  useEffect(() => {
    if (accessChecked && hasAccess) {
      loadData(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessChecked, hasAccess]);

  const handleApply = () => loadData(0);
  const handleReset = () => {
    setDateFrom("");
    setDateTo("");
    setUserFilter("");
    setUserSearch("");
    setPageFilter("");
  };
  const handleLoadMore = () => loadData(offset + PAGE_SIZE);

  // After reset, re-fetch with cleared filters
  useEffect(() => {
    if (accessChecked && hasAccess && !dateFrom && !dateTo && !userFilter && !pageFilter && data) {
      loadData(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, userFilter, pageFilter]);

  // ── Access check loading
  if (!accessChecked) {
    return (
      <div className={styles.page}>
        <div className={styles.centerWrap}>
          <Spinner size={SpinnerSize.large} label="Checking access..." />
        </div>
      </div>
    );
  }

  // ── Access denied
  if (!hasAccess) {
    return (
      <div className={styles.page}>
        <div className={styles.container}>
          <div style={{ padding: 40, textAlign: "center" }}>
            <MessageBar messageBarType={MessageBarType.blocked} isMultiline={false}>
              Analytics is available to administrators only.
            </MessageBar>
          </div>
        </div>
      </div>
    );
  }

  // Build page filter options
  const pageOptions: IDropdownOption[] = [
    { key: "", text: "All Pages" },
    ...(data?.pages || []).map((p) => ({ key: p, text: p })),
  ];

  // Table columns
  const columns: IColumn[] = [
    {
      key: "user",
      name: "User",
      minWidth: 160,
      maxWidth: 240,
      onRender: (item: ActivityRow) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: 13 }}>
            {item.user_name || item.user_upn.split("@")[0]}
          </div>
          <div style={{ fontSize: 11, color: boroondaraPalette.text3 }}>
            {item.user_upn}
          </div>
        </div>
      ),
    },
    {
      key: "page",
      name: "Page",
      minWidth: 100,
      maxWidth: 140,
      onRender: (item: ActivityRow) =>
        item.page ? <span className={styles.pill}>{item.page}</span> : <span style={{ color: "#999" }}>-</span>,
    },
    {
      key: "action",
      name: "Action",
      minWidth: 150,
      maxWidth: 280,
      onRender: (item: ActivityRow) => (
        <span style={{ fontSize: 12, fontFamily: "monospace" }}>
          <span style={{ fontWeight: 600 }}>{item.method}</span>{" "}
          {item.path.length > 50 ? item.path.substring(0, 50) + "..." : item.path}
        </span>
      ),
    },
    {
      key: "duration",
      name: "Duration",
      minWidth: 70,
      maxWidth: 90,
      onRender: (item: ActivityRow) => (
        <span style={{ fontSize: 12 }}>
          {item.duration_ms != null ? `${item.duration_ms}ms` : "-"}
        </span>
      ),
    },
    {
      key: "status",
      name: "Status",
      minWidth: 50,
      maxWidth: 60,
      onRender: (item: ActivityRow) => (
        <span className={item.status_code < 400 ? styles.statusOk : styles.statusErr}>
          {item.status_code}
        </span>
      ),
    },
    {
      key: "timestamp",
      name: "Timestamp",
      minWidth: 140,
      maxWidth: 180,
      onRender: (item: ActivityRow) => (
        <span style={{ fontSize: 12, color: boroondaraPalette.text2 }}>
          {new Date(item.created_at).toLocaleString("en-AU", {
            day: "numeric",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          })}
        </span>
      ),
    },
  ];

  // Format chart data
  const chartData = (data?.dailyViews || []).map((d) => ({
    day: new Date(d.day).toLocaleDateString("en-AU", { day: "numeric", month: "short" }),
    views: d.views,
  }));

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.headerRow}>
          <Text variant="xxLarge" className={styles.title}>
            Portal Analytics
          </Text>
          <Text variant="medium" className={styles.subtitle}>
            Track portal usage, active users, and page views across the Enterprise Data Portal.
          </Text>
        </div>

        {/* Filter Bar */}
        <div className={styles.filterBar}>
          <div className={styles.filterField}>
            <TextField
              label="From"
              type="date"
              value={dateFrom}
              onChange={(_, v) => setDateFrom(v || "")}
              styles={{ root: { minWidth: 140 } }}
            />
          </div>
          <div className={styles.filterField}>
            <TextField
              label="To"
              type="date"
              value={dateTo}
              onChange={(_, v) => setDateTo(v || "")}
              styles={{ root: { minWidth: 140 } }}
            />
          </div>
          <div className={styles.userSearchWrap} ref={userSearchRef}>
            <TextField
              label="User"
              placeholder="Search users..."
              value={userSearch || userFilter}
              onChange={(_, v) => {
                setUserSearch(v || "");
                if (!v) setUserFilter("");
                setShowUserSuggestions(true);
              }}
              onFocus={() => setShowUserSuggestions(true)}
              iconProps={userFilter ? {
                iconName: "Cancel",
                style: { cursor: "pointer", fontSize: 12 },
                onClick: () => { setUserFilter(""); setUserSearch(""); },
              } as any : undefined}
              styles={{ root: { minWidth: 180 } }}
            />
            {showUserSuggestions && userSearchRef.current && (
              <Callout
                target={userSearchRef.current}
                isBeakVisible={false}
                directionalHint={DirectionalHint.bottomLeftEdge}
                onDismiss={() => setShowUserSuggestions(false)}
                calloutWidth={280}
                styles={{ root: { borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.12)" } }}
              >
                <div className={styles.suggestionList}>
                  {filteredSuggestions.length > 0 ? (
                    filteredSuggestions.slice(0, 20).map((u) => (
                      <div
                        key={u.upn}
                        className={styles.suggestionItem}
                        onClick={() => {
                          setUserFilter(u.upn);
                          setUserSearch(u.name);
                          setShowUserSuggestions(false);
                        }}
                      >
                        <div>
                          <span className={styles.suggestionName}>{u.name}</span>
                          {u.role && (
                            <span
                              className={styles.suggestionRole}
                              style={{
                                background: u.role === "admin" ? "#d9f4d7" : "#F0F0F0",
                                color: u.role === "admin" ? "#2e873d" : "#747474",
                              }}
                            >
                              {u.role}
                            </span>
                          )}
                        </div>
                        <span className={styles.suggestionUpn}>{u.upn}</span>
                      </div>
                    ))
                  ) : (
                    <div className={styles.suggestionEmpty}>No matching users</div>
                  )}
                </div>
              </Callout>
            )}
          </div>
          <div className={styles.filterField}>
            <Dropdown
              label="Page"
              selectedKey={pageFilter}
              options={pageOptions}
              onChange={(_, opt) => setPageFilter((opt?.key as string) || "")}
              styles={{ root: { minWidth: 140 } }}
            />
          </div>
          <PrimaryButton
            text="Apply"
            onClick={handleApply}
            styles={{
              root: {
                background: boroondaraPalette.primary,
                borderColor: boroondaraPalette.primary,
                borderRadius: 4,
                height: 32,
                marginTop: 6,
              },
              rootHovered: { background: boroondaraPalette.primaryHover },
            }}
          />
          <DefaultButton
            text="Reset"
            onClick={handleReset}
            styles={{ root: { borderRadius: 4, height: 32, marginTop: 6 } }}
          />
        </div>

        {loading && !data ? (
          <div className={styles.centerWrap}>
            <Spinner size={SpinnerSize.large} label="Loading analytics..." />
          </div>
        ) : error ? (
          <MessageBar messageBarType={MessageBarType.error} isMultiline={false}>
            {error}
          </MessageBar>
        ) : data ? (
          <>
            {/* KPI Cards */}
            <div className={styles.kpiRow}>
              <div className={styles.kpiCard}>
                <div className={styles.kpiIconWrap} style={{ background: "rgba(46,135,61,0.12)", border: "1px solid #2e873d30" }}>
                  <Eye size={16} color="#2e873d" />
                </div>
                <div className={styles.kpiLabel}>Total Page Views</div>
                <div className={styles.kpiValue}>{(data.kpis.totalPageViews || 0).toLocaleString()}</div>
              </div>
              <div className={styles.kpiCard}>
                <div className={styles.kpiIconWrap} style={{ background: "rgba(56,189,248,0.12)", border: "1px solid #38BDF830" }}>
                  <UsersThree size={16} color="#38BDF8" />
                </div>
                <div className={styles.kpiLabel}>Active Users</div>
                <div className={styles.kpiValue}>{(data.kpis.uniqueUsers || 0).toLocaleString()}</div>
              </div>
              <div className={styles.kpiCard}>
                <div className={styles.kpiIconWrap} style={{ background: "rgba(124,124,255,0.12)", border: "1px solid #7C7CFF30" }}>
                  <Plugs size={16} color="#7C7CFF" />
                </div>
                <div className={styles.kpiLabel}>Sessions</div>
                <div className={styles.kpiValue}>{(data.kpis.uniqueSessions || 0).toLocaleString()}</div>
              </div>
              <div className={styles.kpiCard}>
                <div className={styles.kpiIconWrap} style={{ background: "rgba(251,191,36,0.12)", border: "1px solid #FBBF2430" }}>
                  <Star size={16} color="#FBBF24" />
                </div>
                <div className={styles.kpiLabel}>Most Active Page</div>
                <div className={styles.kpiValue} style={{ fontSize: 18 }}>{data.kpis.mostActivePage || "N/A"}</div>
              </div>
            </div>

            {/* Daily Views Chart */}
            <div className={styles.chartCard}>
              <div className={styles.chartTitle}>
                <ChartBar size={16} color="#2e873d" />
                Page Views Over Time
              </div>
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#EDEDED" />
                    <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#484848" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#747474" }} allowDecimals={false} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Bar dataKey="views" fill="#2e873d" radius={[4, 4, 0, 0]} barSize={32} name="Views" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className={styles.noData}>No activity data yet. Browse the portal to generate data.</div>
              )}
            </div>

            {/* Activity Log Table */}
            <div className={styles.tableCard}>
              <div className={styles.chartTitle}>
                <TrendUp size={16} color="#7C7CFF" />
                Activity Log
                <span style={{ fontSize: 12, color: boroondaraPalette.text3, fontWeight: 400, marginLeft: 8 }}>
                  {data.totalCount.toLocaleString()} total entries
                </span>
              </div>
              {data.activity.length > 0 ? (
                <>
                  <DetailsList
                    items={data.activity}
                    columns={columns}
                    layoutMode={DetailsListLayoutMode.justified}
                    selectionMode={SelectionMode.none}
                    isHeaderVisible
                    compact
                  />
                  {data.activity.length < data.totalCount && (
                    <div className={styles.loadMore}>
                      <DefaultButton
                        text={loading ? "Loading..." : "Load More"}
                        onClick={handleLoadMore}
                        disabled={loading}
                        styles={{ root: { borderRadius: 4 } }}
                      />
                    </div>
                  )}
                </>
              ) : (
                <div className={styles.noData}>No activity records found.</div>
              )}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
